import { createApi } from '@reduxjs/toolkit/query/react';

import {
  AddGroupUsersPayload,
  AdminCreateGroupPayload,
  AdminEditGroupPayload,
  AdminFiltersResponse,
  AdminGroupsFiltersByIdPayload,
  AdminGroupsListResponse,
  AdminGroupsQuery,
  AdminGroupsResponse,
  AdminGroupsStatsDatesPayload,
  AdminGroupsStatsDatesResponse,
  AdminGroupsStatsQuery,
  AdminGroupsStatsResponse,
  AdminGroupTestsFilters,
  AdminUpdateUserPayload,
  AdminUserEmailsPayload,
  AdminUserEmailsResponse,
  AdminUsersPayload,
  AdminUsersResponse,
  AdminUserTestResponse,
  AdminUserTestsFiltersResponse,
  AdminUserTestsQuery,
  BillingResponse,
  BillingsGroupPayload,
  BillingsGroupResponse,
  BillingsPayload,
  CreateAdminUserPayload,
  GetAdminGroupByIdPayload,
  GetAdminGroupByIdResponse,
  GetAdminGroupTestsPayload,
  GetAdminGroupTestsResponse,
  GetAdminGroupUsersPayload,
  GetAdminGroupUsersResponse,
  GetGroupDetailsByIdPayload,
  GetGroupStatsPayload,
  GetGroupStatsResponse,
  RemoveAdminGroupsPayload,
  RemoveUsersGroupPayload,
  ResetGropUsersPasswordsPayload,
  SetAdminsPayload,
  SuccessMessageResponse
} from '../interfaces/admin.interface';
import { baseQueryWithReAuth } from './api';

const BASE_URL = `${import.meta.env.VITE_BACKEND_URL}`;

export const apiAdmin = createApi({
  reducerPath: 'apiAdmin',
  baseQuery: (...args) => baseQueryWithReAuth(...args, { baseUrl: `${BASE_URL}/api/admin` }),
  tagTypes: [
    'AdminUserTests',
    'AdminUserTestsFilters',
    'AdminGroupsStats',
    'AdminGroupsStatsDates',
    'AdminGroupsList',
    'AdminFilters',
    'AdminUsers',
    'AdminGroups',
    'AdminResetPasswords',
    'AdminGroupUsers'
  ],
  endpoints: (build) => ({
    getAdminUserTests: build.query<AdminUserTestResponse, AdminUserTestsQuery>({
      query: ({ id, params }) => ({
        url: `/users/tests/${id}`,
        method: 'GET',
        params
      }),
      providesTags: ['AdminUserTests']
    }),
    getAdminUserTestFilters: build.query<AdminUserTestsFiltersResponse, void>({
      query: () => '/users/tests/filters',
      providesTags: ['AdminUserTestsFilters']
    }),
    getAdminGroups: build.query<AdminGroupsResponse, AdminGroupsQuery>({
      query: (params) => ({
        url: '/group',
        method: 'GET',
        params
      }),
      providesTags: ['AdminGroups']
    }),
    getAdminGroupsList: build.query<AdminGroupsListResponse, void>({
      query: () => '/group/list',
      providesTags: ['AdminGroupsList']
    }),
    getAdminFilters: build.query<AdminFiltersResponse, void>({
      query: () => '/users/filters',
      providesTags: ['AdminFilters']
    }),
    getAdminUsers: build.query<AdminUsersResponse, AdminUsersPayload>({
      query: (params) => ({
        url: '/users',
        method: 'GET',
        params
      }),
      providesTags: ['AdminUsers']
    }),
    exportAdminUserEmails: build.query<AdminUserEmailsResponse, AdminUserEmailsPayload>({
      query: (params) => ({
        url: '/users/export-emails',
        method: 'GET',
        params
      }),
      keepUnusedDataFor: 0
    }),
    getAdminBillings: build.query<BillingResponse, BillingsPayload>({
      query: (params) => ({
        url: '/billings',
        method: 'GET',
        params
      })
    }),
    getAdminGroupBillings: build.query<BillingsGroupResponse, BillingsGroupPayload>({
      query: (params) => ({
        url: `/billings/${params.id}`,
        method: 'GET',
        params
      })
    }),
    getAdminGroupsStatsDates: build.query<AdminGroupsStatsDatesResponse, AdminGroupsStatsDatesPayload>({
      query: ({ group = '' }) => ({
        url: `/stats/dates`,
        params: { group }
      }),
      providesTags: ['AdminGroupsStatsDates']
    }),
    getAdminGroupsStats: build.query<AdminGroupsStatsResponse, AdminGroupsStatsQuery>({
      query: (params) => ({
        url: `/stats`,
        params
      }),
      providesTags: ['AdminGroupsStats']
    }),
    adminCreateUser: build.mutation<SuccessMessageResponse, CreateAdminUserPayload>({
      query: (body) => ({
        url: '/users',
        method: 'POST',
        body
      }),
      invalidatesTags: ['AdminUsers']
    }),
    adminCreateGroup: build.mutation<void, AdminCreateGroupPayload>({
      query: (body) => ({
        url: '/group',
        method: 'POST',
        body
      }),
      invalidatesTags: ['AdminGroups']
    }),
    adminDeleteUser: build.mutation<void, { id: string }>({
      query: ({ id }) => ({
        url: `/users/${id}`,
        method: 'DELETE'
      }),
      invalidatesTags: ['AdminUsers']
    }),
    adminDeleteManyUsers: build.mutation<void, string[]>({
      query: (ids) => ({
        url: `/users`,
        method: 'DELETE',
        body: { ids }
      }),
      invalidatesTags: ['AdminUsers']
    }),
    getGroupStats: build.query<GetGroupStatsResponse, GetGroupStatsPayload>({
      query: (params) => ({
        url: 'group/stats',
        method: 'GET',
        params
      })
    }),
    adminUpdateUser: build.mutation<void, AdminUpdateUserPayload>({
      query: (payload) => ({
        url: `/users/${payload.id}`,
        method: 'PATCH',
        body: payload.data
      }),
      invalidatesTags: ['AdminUsers']
    }),
    getAdminGroupUsersFilters: build.query<AdminFiltersResponse, AdminGroupsFiltersByIdPayload>({
      query: ({ id, filters }) => ({
        url: `/group/${id}/users/filters`,
        ...(filters && { params: { ...filters } })
      }),
      providesTags: ['AdminFilters']
    }),
    getAdminGroupTestsFiltersById: build.query<AdminFiltersResponse, string>({
      query: (id) => ({
        url: `/group/${id}/tests/filters`
      }),
      transformResponse: (response: AdminFiltersResponse): AdminFiltersResponse => {
        if (!response?.filters) {
          return { filters: [] };
        }

        return {
          filters: response.filters.map((filter) => {
            if (filter.name === AdminGroupTestsFilters.TEST_SUITE_ID) {
              return {
                ...filter,
                name: 'Page Type'
              };
            }
            return filter;
          })
        };
      },
      providesTags: ['AdminFilters']
    }),
    getAdminGroupById: build.query<GetAdminGroupByIdResponse, GetAdminGroupByIdPayload>({
      query: ({ id }) => ({
        url: `group/${id}`
      }),
      providesTags: ['AdminGroups']
    }),
    getAdminGroupTests: build.query<GetAdminGroupTestsResponse, GetAdminGroupTestsPayload>({
      query: ({ id, page, limit, dateRange, requesters, testTypes }) => ({
        url: `group/${id}/tests`,
        params: {
          page,
          limit,
          dateRange,
          ...(requesters?.length ? { requesters } : {}),
          ...(testTypes?.length ? { testTypes } : {})
        }
      }),
      providesTags: ['AdminGroups']
    }),

    removeAdminGroups: build.mutation<void, RemoveAdminGroupsPayload>({
      query: ({ ids }) => ({
        url: '/group',
        method: 'DELETE',
        body: {
          ids
        }
      })
    }),
    updateGroup: build.mutation<void, AdminEditGroupPayload>({
      query: ({ id, data }) => ({
        url: `/group/${id}`,
        method: 'PATCH',
        body: data
      }),
      invalidatesTags: ['AdminGroups']
    }),
    getGroupDetailsById: build.query<GetGroupDetailsByIdPayload, string>({
      query: (id) => ({
        url: `/group/${id}`,
        method: 'GET'
      }),
      providesTags: ['AdminGroups']
    }),
    resetGropUsersPasswords: build.mutation<void, ResetGropUsersPasswordsPayload>({
      query: ({ groupId, users }) => ({
        url: `/group/reset-passwords`,
        method: 'POST',
        body: {
          groupId,
          users
        }
      }),
      invalidatesTags: ['AdminResetPasswords']
    }),
    // GROUP USERS
    addGroupUsers: build.mutation<void, AddGroupUsersPayload>({
      query: ({ groupId, users }) => ({
        url: `group/${groupId}/users`,
        method: 'POST',
        body: {
          users
        }
      }),
      invalidatesTags: ['AdminGroupUsers', 'AdminFilters']
    }),
    getAdminGroupUsers: build.query<GetAdminGroupUsersResponse, GetAdminGroupUsersPayload>({
      query: ({ id, ...params }) => ({
        url: `group/${id}/users`,
        params
      }),
      providesTags: ['AdminGroupUsers']
    }),
    setAdminsGroup: build.mutation<void, SetAdminsPayload>({
      query: ({ groupId, users }) => ({
        url: `group/${groupId}/admins`,
        method: 'POST',
        body: {
          users
        }
      }),
      invalidatesTags: ['AdminGroupUsers']
    }),
    removeAdminsGroup: build.mutation<void, SetAdminsPayload>({
      query: ({ groupId, users }) => ({
        url: `group/${groupId}/admins`,
        method: 'DELETE',
        body: {
          users
        }
      }),
      invalidatesTags: ['AdminGroupUsers']
    }),
    removeUsersGroup: build.mutation<void, RemoveUsersGroupPayload>({
      query: ({ groupId, users }) => ({
        url: `group/${groupId}/users`,
        method: 'DELETE',
        body: {
          users
        }
      }),
      invalidatesTags: ['AdminGroupUsers', 'AdminFilters']
    }),
    unblockUser: build.mutation<void, string[]>({
      query: (userIds) => ({
        url: '/unblock-users',
        method: 'POST',
        body: {
          userIds
        }
      }),
      invalidatesTags: ['AdminUsers', 'AdminGroupUsers']
    }),
    reactivateUser: build.mutation<void, string[]>({
      query: (userIds) => ({
        url: '/reactivate-users',
        method: 'POST',
        body: {
          userIds
        }
      }),
      invalidatesTags: ['AdminUsers', 'AdminGroupUsers']
    }),
    disableUserTwoFactor: build.mutation<void, { id: string }>({
      query: ({ id }) => ({
        url: `/users/${id}/disable-2fa`,
        method: 'POST'
      }),
      invalidatesTags: ['AdminUsers']
    })
  })
});

export const {
  useGetAdminUserTestsQuery,
  useGetAdminUserTestFiltersQuery,
  useGetAdminUsersQuery,
  useLazyExportAdminUserEmailsQuery,
  useGetAdminGroupsListQuery,
  useGetAdminFiltersQuery,
  useGetAdminBillingsQuery,
  useAdminDeleteUserMutation,
  useAdminDeleteManyUsersMutation,
  useAdminCreateUserMutation,
  useLazyGetAdminGroupsStatsQuery,
  useGetAdminGroupsStatsDatesQuery,
  useLazyGetAdminGroupBillingsQuery,
  useAdminCreateGroupMutation,
  useGetAdminGroupsQuery,
  useGetGroupStatsQuery,
  useAdminUpdateUserMutation,
  useGetAdminGroupUsersFiltersQuery,
  useGetAdminGroupTestsFiltersByIdQuery,
  useGetAdminGroupByIdQuery,
  useSetAdminsGroupMutation,
  useRemoveAdminsGroupMutation,
  useRemoveUsersGroupMutation,
  useRemoveAdminGroupsMutation,
  useGetAdminGroupUsersQuery,
  useGetAdminGroupTestsQuery,
  useUpdateGroupMutation,
  useGetGroupDetailsByIdQuery,
  useResetGropUsersPasswordsMutation,
  useAddGroupUsersMutation,
  useUnblockUserMutation,
  useReactivateUserMutation,
  useDisableUserTwoFactorMutation
} = apiAdmin;
